import React from "react";

import {
    CircularProgress,
    Button,
    Card,
    CardContent,
    Typography,
    Stack,
    Input,
    FormHelperText,
    Divider,
} from "@mui/joy";
import Box from "@mui/joy/Box";

// components
import BaseApi from "../../utils/BaseApi";

// icons
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";


class ChangePasswordModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            oldPassword: '',
            newPassword: '',
            newPasswordConfirm: '',
            loading: false,
            success: false,
            error: null,
            passwordStrength: 0,
        };
    }

    getPasswordStrength(password) {
        let strength = 0;
        if (password.length >= 8) strength += 1;
        if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength += 1;
        if (password.match(/\d/)) strength += 1;
        if (password.match(/[^a-zA-Z\d]/)) strength += 1;
        return strength;
    }

    handlePasswordChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
        
        if (name === 'newPassword') {
            this.setState({
                passwordStrength: this.getPasswordStrength(value)
            });
        }
    };

    handleSubmit = async (e) => {
        e.preventDefault();
        
        const { oldPassword, newPassword, newPasswordConfirm } = this.state;
        
        // Валидация
        if (!oldPassword || !newPassword) {
            this.setState({ error: 'Заполните все поля' });
            return;
        }
        
        if (newPassword !== newPasswordConfirm) {
            this.setState({ error: 'Пароли не совпадают' });
            return;
        }
        
        if (newPassword.length < 8) {
            this.setState({ error: 'Пароль должен содержать минимум 8 символов' });
            return;
        }
        
        this.setState({ loading: true, error: null, success: false });
        
        const api = new BaseApi();
        
        try {
            const response = await api.post('user/change_password/', {
                old_password: oldPassword,
                new_password: newPassword,
                new_password_confirm: newPasswordConfirm,
            });
            
            if (response.status === 200) {
                this.setState({
                    success: true,
                    loading: false,
                    oldPassword: '',
                    newPassword: '',
                    newPasswordConfirm: '',
                    passwordStrength: 0
                });
                
                // Закрыть модалку через 2 секунды
                setTimeout(() => {
                    this.props.onClose && this.props.onClose();
                }, 2000);
            }
        } catch (error) {
            console.error('Ошибка при смене пароля:', error);
            let errorMsg = 'Ошибка при смене пароля';
            
            if (error.response?.data?.error) {
                if (Array.isArray(error.response.data.error)) {
                    errorMsg = error.response.data.error.join(' ');
                } else {
                    errorMsg = error.response.data.error;
                }
            }
            
            this.setState({ error: errorMsg, loading: false });
        }
    };

    renderPasswordStrength() {
        const { passwordStrength } = this.state;
        const colors = ['danger', 'warning', 'success', 'success'];
        const labels = ['Очень слабый', 'Слабый', 'Средний', 'Сильный'];
        
        if (passwordStrength === 0) return null;
        
        return (
            <Stack spacing={1} sx={{mt: 1}}>
                <Stack direction="row" spacing={1}>
                    {[0, 1, 2, 3].map((level) => (
                        <Box
                            key={level}
                            sx={{
                                height: 4,
                                flexGrow: 1,
                                borderRadius: 1,
                                bgcolor: level < passwordStrength ? colors[passwordStrength - 1] : 'neutral.plainBg',
                            }}
                        />
                    ))}
                </Stack>
                <Typography level="body-xs" color={colors[passwordStrength - 1] || 'neutral'}>
                    {labels[passwordStrength - 1]}
                </Typography>
            </Stack>
        );
    }

    render() {
        const { oldPassword, newPassword, newPasswordConfirm, loading, success, error } = this.state;

        return (
            <Card 
                sx={{
                    width: '100%',
                    maxWidth: 500,
                }}
            >
                <CardContent>
                    <Typography level="title-lg" sx={{mb: 2}}>
                        <LockIcon sx={{mr: 1, verticalAlign: 'middle'}}/>
                        Смена пароля
                    </Typography>
                    
                    {success ? (
                        <Stack spacing={2} sx={{py: 3}}>
                            <CheckCircleIcon color="success" sx={{fontSize: 48, mx: 'auto'}}/>
                            <Typography level="body-lg" sx={{textAlign: 'center'}}>
                                Пароль успешно изменен!
                            </Typography>
                        </Stack>
                    ) : (
                        <form onSubmit={this.handleSubmit}>
                            <Stack spacing={2}>
                                {/* Старый пароль */}
                                <Box>
                                    <Typography level="body-sm" sx={{mb: 1}}>
                                        Текущий пароль
                                    </Typography>
                                    <Input
                                        type="password"
                                        name="oldPassword"
                                        value={oldPassword}
                                        onChange={this.handlePasswordChange}
                                        placeholder="Введите текущий пароль"
                                        startDecorator={<LockIcon fontSize="small"/>}
                                        required
                                        disabled={loading}
                                        size="md"
                                    />
                                </Box>
                                
                                {/* Новый пароль */}
                                <Box>
                                    <Typography level="body-sm" sx={{mb: 1}}>
                                        Новый пароль
                                    </Typography>
                                    <Input
                                        type="password"
                                        name="newPassword"
                                        value={newPassword}
                                        onChange={this.handlePasswordChange}
                                        placeholder="Введите новый пароль"
                                        startDecorator={<LockIcon fontSize="small"/>}
                                        required
                                        disabled={loading}
                                        size="md"
                                    />
                                    {this.renderPasswordStrength()}
                                    <FormHelperText>
                                        Минимум 8 символов, заглавные и строчные буквы, цифры
                                    </FormHelperText>
                                </Box>
                                
                                {/* Подтверждение пароля */}
                                <Box>
                                    <Typography level="body-sm" sx={{mb: 1}}>
                                        Подтвердите новый пароль
                                    </Typography>
                                    <Input
                                        type="password"
                                        name="newPasswordConfirm"
                                        value={newPasswordConfirm}
                                        onChange={this.handlePasswordChange}
                                        placeholder="Повторите новый пароль"
                                        startDecorator={<LockIcon fontSize="small"/>}
                                        required
                                        disabled={loading}
                                        size="md"
                                        error={newPasswordConfirm && newPassword !== newPasswordConfirm}
                                    />
                                    {newPasswordConfirm && newPassword !== newPasswordConfirm && (
                                        <FormHelperText color="danger">
                                            Пароли не совпадают
                                        </FormHelperText>
                                    )}
                                </Box>
                                
                                {/* Ошибки и предупреждения */}
                                {error && (
                                    <FormHelperText color="danger">
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CancelIcon fontSize="small"/>
                                            {error}
                                        </Stack>
                                    </FormHelperText>
                                )}
                                
                                {/* Кнопки */}
                                <Divider sx={{my: 1}}/>
                                
                                <Stack direction="row" spacing={2} justifyContent="flex-end">
                                    {this.props.onClose && (
                                        <Button
                                            variant="plain"
                                            onClick={this.props.onClose}
                                            disabled={loading}
                                        >
                                            Отмена
                                        </Button>
                                    )}
                                    <Button
                                        type="submit"
                                        variant="solid"
                                        color="primary"
                                        disabled={loading}
                                        startDecorator={loading ? <CircularProgress size="sm"/> : <LockIcon/>}
                                    >
                                        {loading ? 'Изменение...' : 'Изменить пароль'}
                                    </Button>
                                </Stack>
                            </Stack>
                        </form>
                    )}
                </CardContent>
            </Card>
        );
    }
}

export default ChangePasswordModal;
